import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { addLayerToggle } from "@/lib/mapLayers";
import { useNavigate } from "react-router-dom";
import {
  Shield, LogOut, Users, Building2, BarChart3, MessageSquare, Activity,
  Globe, Clock, CheckCircle2, AlertTriangle, XCircle, Send, ChevronDown,
  ChevronUp, Eye, User, Radio, TrendingUp, Inbox, Search, Filter, MapPin, Map, X, Ticket
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { isOfficerLive, lastSeenAgo } from "@/lib/officerStatus";
import secureflexLogo from "@/assets/secureflex-logo-new.png";
import ThemeToggle from "@/components/ThemeToggle";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

import AdminPromoManager from "@/components/app/AdminPromoManager";
import MapAddressSearch from "@/components/app/MapAddressSearch";

const adminTabs = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "map", label: "Map", icon: Map },
  { id: "users", label: "Users", icon: Users },
  { id: "promos", label: "Promos", icon: Ticket },
  { id: "system-admins", label: "System Admins", icon: Shield },
  { id: "activity", label: "Activity", icon: Activity },
  { id: "support", label: "Support", icon: MessageSquare },
];

// types 
interface PlatformStats {
  totalBusinessOwners: number;
  totalSiteOwners: number;
  totalOfficers: number;
  totalSites: number;
  totalCheckIns: number;
  totalIncidents: number;
  totalTicketsOpen: number;
  liveOfficers: number;
}

interface UserRow {
  id: string;
  full_name: string | null;
  email: string | null;
  country: string | null;
  last_login_at: string | null;
  created_at: string;
  role: string;
}

interface TicketRow {
  id: string;
  subject: string;
  message: string;
  status: string;
  priority: string;
  user_name: string | null;
  user_email: string | null;
  user_role: string | null;
  created_at: string;
  admin_response: string | null;
  responded_at: string | null;
  user_id: string;
}

interface ActivityRow {
  id: string;
  type: "check_in" | "incident" | "signup" | "face_verify";
  description: string;
  timestamp: string;
  user_name: string | null;
  site_name: string | null;
}

const timeAgo = (d: string) => {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

type MapOfficer = { id: string; name: string; status: string; last_seen: string | null; lat: number | null; lng: number | null; site_id: string | null; phone: string | null; email: string | null; psira: string | null; checkins: number; coverage: number; face_photo_url: string | null; username: string | null };
type MapSite = { id: string; name: string; lat: number | null; lng: number | null; address: string | null };
type MapCheckpoint = { id: string; name: string; lat: number | null; lng: number | null; site_id: string; zone: string | null; radius: number | null; method: string };

const AdminMapView = ({ officers, sites, checkpoints, onCheckpointMoved }: { officers: MapOfficer[]; sites: MapSite[]; checkpoints: MapCheckpoint[]; onCheckpointMoved: (id: string, lat: number, lng: number) => void }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const [selectedOfficer, setSelectedOfficer] = useState<MapOfficer | null>(null);

  const siteMap = useMemo(() => Object.fromEntries(sites.map(s => [s.id, s.name])), [sites]);

  useEffect(() => {
    if (!mapRef.current) return;
    let cancelled = false;
    const loadMap = async () => {
      const L = await import("leaflet");
      if (cancelled || !mapRef.current) return;
      if (!document.getElementById("leaflet-css")) {
        const link = document.createElement("link");
        link.id = "leaflet-css";
        link.rel = "stylesheet";
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
        await new Promise(r => setTimeout(r, 300));
      }
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
      mapRef.current.innerHTML = "";

      const bounds: [number, number][] = [];
      officers.forEach(o => { if (o.lat != null && o.lng != null) bounds.push([o.lat, o.lng]); });
      sites.forEach(s => { if (s.lat != null && s.lng != null) bounds.push([s.lat, s.lng]); });
      const center = bounds.length > 0 ? bounds[0] : [-29.884, 30.947] as [number, number];

      const map = L.map(mapRef.current).setView(center, 13);
      mapInstanceRef.current = map;

      L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", { attribution: '&copy; CARTO', maxZoom: 19 }).addTo(map);
      addLayerToggle(L, map);

      // Site markers
      sites.forEach(s => {
        if (s.lat == null || s.lng == null) return;
        const icon = L.divIcon({
          html: '<div style="width:18px;height:18px;border-radius:4px;background:#F0B429;border:2px solid white;box-shadow:0 0 8px rgba(240,180,41,0.5);display:flex;align-items:center;justify-content:center;"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg></div>',
          iconSize: [18, 18], iconAnchor: [9, 9], className: '',
        });
        L.marker([s.lat, s.lng], { icon }).addTo(map)
          .bindPopup(`<div style="color:#000"><b>${s.name}</b><br/>${s.address || 'No address'}</div>`)
          .bindTooltip(s.name, { permanent: false, direction: "top", offset: [0, -12], className: "site-label" });
      });

      // Officer markers
      officers.forEach(o => {
        if (o.lat == null || o.lng == null) return;
        const staleMins = o.last_seen ? (Date.now() - new Date(o.last_seen).getTime()) / 60000 : Infinity;
        const live = isOfficerLive(o);
        const color = live ? '#10B981' : '#6B7280';
        const icon = L.divIcon({
          html: `<div style="position:relative;width:${live ? 22 : 16}px;height:${live ? 22 : 16}px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 0 10px ${color}80;cursor:pointer;">${live ? '<div style="position:absolute;inset:-4px;border-radius:50%;border:2px solid rgba(16,185,129,0.3);animation:pulse 2s infinite;"></div>' : ''}</div>`,
          iconSize: [live ? 22 : 16, live ? 22 : 16], iconAnchor: [live ? 11 : 8, live ? 11 : 8], className: '',
        });
        const marker = L.marker([o.lat, o.lng], { icon }).addTo(map)
          .bindTooltip(o.name, { permanent: true, direction: "top", offset: [0, -14], className: "officer-name-label" });
        marker.on("click", () => setSelectedOfficer(o));
      });

      // Checkpoint markers (draggable)
      checkpoints.forEach(cp => {
        if (cp.lat == null || cp.lng == null) return;
        bounds.push([cp.lat, cp.lng]);
        const cpIcon = L.divIcon({
          html: `<div style="width:14px;height:14px;border-radius:50%;background:#EF4444;border:2px solid white;box-shadow:0 0 6px rgba(239,68,68,0.4);cursor:grab;"></div>`,
          iconSize: [14, 14], iconAnchor: [7, 7], className: '',
        });
        const cpMarker = L.marker([cp.lat, cp.lng], { icon: cpIcon, draggable: true }).addTo(map)
          .bindPopup(`<div style="color:#000"><b>ðŸ“ ${cp.name}</b><br/>Site: ${siteMap[cp.site_id] || '—'}<br/>${cp.zone || ''}<br/><i style="font-size:10px;color:#666">Drag to reposition</i></div>`)
          .bindTooltip(cp.name, { permanent: false, direction: "top", offset: [0, -10], className: "site-label" });
        if (cp.method === "gps" && (cp.radius || 50) > 0) {
          const circle = L.circle([cp.lat, cp.lng], { radius: cp.radius || 50, color: '#EF4444', fillColor: 'rgba(239,68,68,0.08)', fillOpacity: 0.3, weight: 1, dashArray: '4 4' }).addTo(map);
          cpMarker.on("drag", (e: any) => {
            const pos = e.target.getLatLng();
            circle.setLatLng(pos);
          });
        }
        cpMarker.on("dragend", (e: any) => {
          const pos = e.target.getLatLng();
          onCheckpointMoved(cp.id, pos.lat, pos.lng);
        });
      });

      if (bounds.length > 1) {
        try { map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 }); } catch {}
      }
      setTimeout(() => map.invalidateSize(), 200);
    };
    loadMap();
    return () => { cancelled = true; if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
  }, [officers, sites, checkpoints]);

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
        <MapPin className="w-5 h-5 text-[hsl(var(--app-gold))]" /> Platform Map
        <span className="text-xs font-normal text-muted-foreground ml-2">
          {officers.filter(o => o.lat != null).length} officers · {sites.filter(s => s.lat != null).length} sites · {checkpoints.filter(c => c.lat != null).length} checkpoints
        </span>
      </h2>

      {/* Address search */}
      <MapAddressSearch mapInstance={mapInstanceRef.current} />

      <div className="relative">
        <div ref={mapRef} className="w-full h-[500px] rounded-xl border border-[hsl(var(--app-border-subtle))] overflow-hidden bg-[hsl(var(--app-surface-2))]" />

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-[hsl(var(--app-surface-1)/0.95)] backdrop-blur-sm rounded-lg border border-[hsl(var(--app-border-subtle))] p-3 text-xs space-y-1.5 z-[1000]">
          <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[hsl(var(--app-green))]" /> Live Officer</div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-muted-foreground/50" /> Offline Officer</div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-[hsl(var(--app-gold))]" style={{ width: 12, height: 12 }} /> Site</div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-destructive" /> Checkpoint (drag to move)</div>
        </div>

        {/* Officer detail panel */}
        {selectedOfficer && (
          <div className="absolute top-4 right-4 w-80 bg-[hsl(var(--app-surface-1)/0.98)] backdrop-blur-sm rounded-xl border border-[hsl(var(--app-border-subtle))] shadow-xl z-[1000] overflow-hidden">
            <div className="p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {selectedOfficer.face_photo_url ? (
                    <img src={selectedOfficer.face_photo_url} alt={selectedOfficer.name} className="w-12 h-12 rounded-full object-cover border-2 border-[hsl(var(--app-gold)/0.3)]" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[hsl(var(--app-surface-2))] flex items-center justify-center text-sm font-bold text-muted-foreground border-2 border-[hsl(var(--app-border-subtle))]">
                      {selectedOfficer.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-bold text-foreground">{selectedOfficer.name}</p>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      isOfficerLive(selectedOfficer)
                        ? "bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))]"
                        : "bg-[hsl(var(--app-surface-2))] text-muted-foreground"
                    }`}>
                      {isOfficerLive(selectedOfficer) ? "â— LIVE" : "○  OFFLINE"}
                    </span>
                  </div>
                </div>
                <button onClick={() => setSelectedOfficer(null)} className="p-1 rounded-lg hover:bg-[hsl(var(--app-surface-2))] text-muted-foreground hover:text-foreground transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[hsl(var(--app-surface-2))] rounded-lg p-2.5">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Check-ins</p>
                  <p className="text-lg font-bold text-[hsl(var(--app-gold))]">{selectedOfficer.checkins}</p>
                </div>
                <div className="bg-[hsl(var(--app-surface-2))] rounded-lg p-2.5">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Coverage</p>
                  <p className="text-lg font-bold text-[hsl(var(--app-blue))]">{selectedOfficer.coverage}%</p>
                </div>
              </div>

              <div className="space-y-2 text-xs">
                {selectedOfficer.username && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Username</span><span className="font-medium text-foreground">{selectedOfficer.username}</span></div>
                )}
                {selectedOfficer.email && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="font-medium text-foreground truncate ml-2">{selectedOfficer.email}</span></div>
                )}
                {selectedOfficer.phone && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span className="font-medium text-foreground">{selectedOfficer.phone}</span></div>
                )}
                {selectedOfficer.psira && (
                  <div className="flex justify-between"><span className="text-muted-foreground">PSIRA</span><span className="font-medium text-foreground font-mono">{selectedOfficer.psira}</span></div>
                )}
                <div className="flex justify-between"><span className="text-muted-foreground">Assigned Site</span><span className="font-medium text-foreground">{selectedOfficer.site_id ? siteMap[selectedOfficer.site_id] || "—" : "Unassigned"}</span></div>
                {selectedOfficer.last_seen && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Last Seen</span><span className="font-medium text-foreground">{timeAgo(selectedOfficer.last_seen)}</span></div>
                )}
                {selectedOfficer.lat != null && selectedOfficer.lng != null && (
                  <div className="flex justify-between"><span className="text-muted-foreground">GPS</span><span className="font-mono text-[10px] text-foreground">{selectedOfficer.lat.toFixed(6)}, {selectedOfficer.lng.toFixed(6)}</span></div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


const AdminDashboard = () => {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState<PlatformStats>({
    totalBusinessOwners: 0, totalSiteOwners: 0, totalOfficers: 0,
    totalSites: 0, totalCheckIns: 0, totalIncidents: 0, totalTicketsOpen: 0, liveOfficers: 0,
  });
  const [users, setUsers] = useState<UserRow[]>([]);
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [activities, setActivities] = useState<ActivityRow[]>([]);
  const [userFilter, setUserFilter] = useState("all");
  const [ticketFilter, setTicketFilter] = useState("open");
  const [searchQuery, setSearchQuery] = useState("");
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [loading, setLoading] = useState(true);

  // Raw data for charts & drill-down
  const [rawCheckIns, setRawCheckIns] = useState<{ checked_at: string; officer_id: string; site_id: string | null }[]>([]);
  const [rawIncidents, setRawIncidents] = useState<{ id: string; title: string; severity: string; created_at: string; site_id: string | null }[]>([]);
  const [rawOfficers, setRawOfficers] = useState<{ id: string; name: string; status: string; last_seen: string | null; lat: number | null; lng: number | null; site_id: string | null; phone: string | null; email: string | null; psira: string | null; checkins: number; coverage: number; face_photo_url: string | null; username: string | null }[]>([]);
  const [rawSites, setRawSites] = useState<{ id: string; name: string; lat: number | null; lng: number | null; address: string | null }[]>([]);
  const [rawCheckpoints, setRawCheckpoints] = useState<MapCheckpoint[]>([]);
  const [expandedTile, setExpandedTile] = useState<string | null>(null);

  // Fetch all data
  useEffect(() => {
    // Clean up stale officer statuses
    supabase.functions.invoke("cleanup-stale-officers").catch(() => {});
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [rolesRes, officersRes, sitesRes, checkInsRes, incidentsRes, ticketsRes, profilesRes, checkpointsRes] = await Promise.all([
          supabase.from("user_roles").select("user_id, role"),
          supabase.from("officers").select("id, name, status, last_seen, site_id, lat, lng, phone, email, psira, checkins, coverage, face_photo_url, username"),
          supabase.from("sites").select("id, name, lat, lng, address"),
          supabase.from("check_ins").select("id, checked_at, officer_id, site_id").order("checked_at", { ascending: false }).limit(200),
          supabase.from("incidents").select("id, title, severity, created_at, officer_id, site_id").order("created_at", { ascending: false }).limit(200),
          supabase.from("support_tickets").select("*").order("created_at", { ascending: false }),
          supabase.from("profiles").select("id, full_name, email, country, last_login_at, created_at"),
          supabase.from("checkpoints").select("id, name, lat, lng, site_id, zone, radius, method"),
        ]);

        const roles = rolesRes.data || [];
        const officers = officersRes.data || [];
        const sites = sitesRes.data || [];
        const checkIns = checkInsRes.data || [];
        const incidents = incidentsRes.data || [];
        const allTickets = (ticketsRes.data || []) as unknown as TicketRow[];
        const profiles = profilesRes.data || [];

        const boCount = roles.filter(r => r.role === "business_owner").length;
        const soCount = roles.filter(r => r.role === "site_owner").length;
        const liveCount = officers.filter(o => isOfficerLive(o)).length;

        setStats({
          totalBusinessOwners: boCount,
          totalSiteOwners: soCount,
          totalOfficers: officers.length,
          totalSites: sites.length,
          totalCheckIns: checkIns.length,
          totalIncidents: incidents.length,
          totalTicketsOpen: allTickets.filter(t => t.status === "open").length,
          liveOfficers: liveCount,
        });

        // Build user list with roles
        const userRows: UserRow[] = profiles.map(p => {
          const userRole = roles.find(r => r.user_id === p.id);
          return {
            id: p.id,
            full_name: p.full_name,
            email: p.email,
            country: p.country,
            last_login_at: p.last_login_at,
            created_at: p.created_at,
            role: userRole?.role || "unknown",
          };
        });
        setUsers(userRows);
        setTickets(allTickets);
        setRawCheckIns(checkIns.map(ci => ({ checked_at: ci.checked_at, officer_id: ci.officer_id, site_id: ci.site_id || null })));
        setRawIncidents(incidents.map(inc => ({ id: inc.id, title: inc.title, severity: inc.severity, created_at: inc.created_at, site_id: inc.site_id || null })));
        setRawOfficers(officers as any);
        setRawSites(sites as any);
        setRawCheckpoints((checkpointsRes.data || []) as MapCheckpoint[]);

        // Build activity feed
        const siteMap = Object.fromEntries(sites.map(s => [s.id, s.name]));
        const officerMap = Object.fromEntries(officers.map(o => [o.id, o.name]));
        const acts: ActivityRow[] = [];

        checkIns.slice(0, 50).forEach(ci => {
          acts.push({
            id: ci.id,
            type: "check_in",
            description: `Check-in completed`,
            timestamp: ci.checked_at,
            user_name: officerMap[ci.officer_id] || "Officer",
            site_name: ci.site_id ? siteMap[ci.site_id] || null : null,
          });
        });

        incidents.slice(0, 50).forEach(inc => {
          acts.push({
            id: inc.id,
            type: "incident",
            description: inc.title,
            timestamp: inc.created_at,
            user_name: inc.officer_id ? officerMap[inc.officer_id] || null : null,
            site_name: inc.site_id ? siteMap[inc.site_id] || null : null,
          });
        });

        acts.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        setActivities(acts.slice(0, 100));
      } catch (err) {
        console.error("Admin fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate("/app");
  };

  const handleReply = async (ticketId: string) => {
    if (!replyText.trim()) return;
    const { error } = await supabase
      .from("support_tickets")
      .update({
        admin_response: replyText.trim(),
        responded_at: new Date().toISOString(),
        status: "resolved",
      } as any)
      .eq("id", ticketId);

    if (error) {
      toast.error("Failed to send reply");
      return;
    }
    toast.success("Reply sent & ticket resolved");
    setReplyingTo(null);
    setReplyText("");
    setTickets(prev => prev.map(t => t.id === ticketId ? {
      ...t, admin_response: replyText.trim(), responded_at: new Date().toISOString(), status: "resolved"
    } : t));
  };

  const filteredUsers = useMemo(() => {
    let list = users;
    if (userFilter !== "all") list = list.filter(u => u.role === userFilter);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(u =>
        (u.full_name?.toLowerCase().includes(q)) ||
        (u.email?.toLowerCase().includes(q)) ||
        (u.country?.toLowerCase().includes(q))
      );
    }
    return list;
  }, [users, userFilter, searchQuery]);

  const filteredTickets = useMemo(() => {
    if (ticketFilter === "all") return tickets;
    return tickets.filter(t => t.status === ticketFilter);
  }, [tickets, ticketFilter]);

  // Chart data 
  const checkInTrend = useMemo(() => {
    const dayMap: Record<string, number> = {};
    const now = new Date();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      dayMap[d.toISOString().slice(0, 10)] = 0;
    }
    rawCheckIns.forEach(ci => {
      const day = ci.checked_at.slice(0, 10);
      if (day in dayMap) dayMap[day]++;
    });
    return Object.entries(dayMap).map(([date, count]) => ({
      date: new Date(date).toLocaleDateString("en", { month: "short", day: "numeric" }),
      checkIns: count,
    }));
  }, [rawCheckIns]);

  const incidentsBySeverity = useMemo(() => {
    const counts: Record<string, number> = { low: 0, medium: 0, high: 0, critical: 0 };
    rawIncidents.forEach(inc => {
      const s = inc.severity?.toLowerCase() || "low";
      if (s in counts) counts[s]++;
      else counts.low++;
    });
    return Object.entries(counts)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }));
  }, [rawIncidents]);

  const officerActivityHeatmap = useMemo(() => {
    const officerCheckins: Record<string, number> = {};
    rawCheckIns.forEach(ci => {
      officerCheckins[ci.officer_id] = (officerCheckins[ci.officer_id] || 0) + 1;
    });
    return rawOfficers
      .map(o => ({ name: o.name.length > 12 ? o.name.slice(0, 12) + "…" : o.name, checkIns: officerCheckins[o.id] || 0 }))
      .sort((a, b) => b.checkIns - a.checkIns)
      .slice(0, 10);
  }, [rawCheckIns, rawOfficers]);

  const SEVERITY_COLORS = ["hsl(42, 87%, 55%)", "hsl(30, 90%, 55%)", "hsl(0, 84%, 60%)", "hsl(0, 70%, 40%)"];


  const statCards = [
    { id: "business_owners", label: "Security Operators", value: stats.totalBusinessOwners, icon: Building2, color: "hsl(var(--app-gold))" },
    { id: "site_owners", label: "Site Operators", value: stats.totalSiteOwners, icon: Globe, color: "hsl(var(--app-blue))" },
    { id: "officers", label: "Officers", value: stats.totalOfficers, icon: Users, color: "hsl(var(--app-green))" },
    { id: "live", label: "Live Now", value: stats.liveOfficers, icon: Radio, color: "hsl(160, 75%, 39%)" },
    { id: "sites", label: "Sites", value: stats.totalSites, icon: Building2, color: "hsl(var(--app-gold))" },
    { id: "checkins", label: "Check-ins", value: stats.totalCheckIns, icon: CheckCircle2, color: "hsl(var(--app-green))" },
    { id: "incidents", label: "Incidents", value: stats.totalIncidents, icon: AlertTriangle, color: "hsl(var(--app-red))" },
    { id: "tickets", label: "Open Tickets", value: stats.totalTicketsOpen, icon: Inbox, color: "hsl(var(--app-blue))" },
  ];

  const siteMap = useMemo(() => Object.fromEntries(rawSites.map(s => [s.id, s.name])), [rawSites]);
  const officerMap = useMemo(() => Object.fromEntries(rawOfficers.map(o => [o.id, o.name])), [rawOfficers]);

  const renderTileDetail = (tileId: string) => {
    switch (tileId) {
      case "business_owners":
        return (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {users.filter(u => u.role === "business_owner").map(u => (
              <div key={u.id} className="flex items-center justify-between py-1.5 border-b border-[hsl(var(--app-border-subtle))] last:border-0">
                <div>
                  <p className="text-xs font-medium text-foreground">{u.full_name || "—"}</p>
                  <p className="text-[10px] text-muted-foreground">{u.email}</p>
                </div>
                <span className="text-[10px] text-muted-foreground">{u.country || "—"}</span>
              </div>
            ))}
            {users.filter(u => u.role === "business_owner").length === 0 && <p className="text-xs text-muted-foreground">None</p>}
          </div>
        );
      case "site_owners":
        return (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {users.filter(u => u.role === "site_owner").map(u => (
              <div key={u.id} className="flex items-center justify-between py-1.5 border-b border-[hsl(var(--app-border-subtle))] last:border-0">
                <div>
                  <p className="text-xs font-medium text-foreground">{u.full_name || "—"}</p>
                  <p className="text-[10px] text-muted-foreground">{u.email}</p>
                </div>
                <span className="text-[10px] text-muted-foreground">{u.last_login_at ? timeAgo(u.last_login_at) : "Never"}</span>
              </div>
            ))}
            {users.filter(u => u.role === "site_owner").length === 0 && <p className="text-xs text-muted-foreground">None</p>}
          </div>
        );
      case "officers":
        return (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {rawOfficers.map(o => (
              <div key={o.id} className="flex items-center justify-between py-1.5 border-b border-[hsl(var(--app-border-subtle))] last:border-0">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${isOfficerLive(o) ? "bg-[hsl(var(--app-green))]" : "bg-muted-foreground/40"}`} />
                  <p className="text-xs font-medium text-foreground">{o.name}</p>
                </div>
                <span className="text-[10px] text-muted-foreground">{o.last_seen ? timeAgo(o.last_seen) : "—"}</span>
              </div>
            ))}
            {rawOfficers.length === 0 && <p className="text-xs text-muted-foreground">None</p>}
          </div>
        );
      case "live":
        const liveOfficers = rawOfficers.filter(o => o.status === "live" && o.last_seen && (Date.now() - new Date(o.last_seen).getTime()) < 300000);
        return (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {liveOfficers.map(o => (
              <div key={o.id} className="flex items-center justify-between py-1.5 border-b border-[hsl(var(--app-border-subtle))] last:border-0">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[hsl(var(--app-green))] animate-pulse" />
                  <p className="text-xs font-medium text-foreground">{o.name}</p>
                </div>
                <span className="text-[10px] text-muted-foreground">{o.last_seen ? timeAgo(o.last_seen) : "Active"}</span>
              </div>
            ))}
            {liveOfficers.length === 0 && <p className="text-xs text-muted-foreground">No officers currently live</p>}
          </div>
        );
      case "sites":
        return (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {rawSites.map(s => {
              const assignedOfficers = rawOfficers.filter(o => rawCheckIns.some(ci => ci.site_id === s.id && ci.officer_id === o.id));
              return (
                <div key={s.id} className="flex items-center justify-between py-1.5 border-b border-[hsl(var(--app-border-subtle))] last:border-0">
                  <p className="text-xs font-medium text-foreground">{s.name}</p>
                  <span className="text-[10px] text-muted-foreground">{assignedOfficers.length} officer{assignedOfficers.length !== 1 ? "s" : ""} active</span>
                </div>
              );
            })}
            {rawSites.length === 0 && <p className="text-xs text-muted-foreground">No sites</p>}
          </div>
        );
      case "checkins":
        return (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {rawCheckIns.slice(0, 20).map((ci, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-[hsl(var(--app-border-subtle))] last:border-0">
                <div>
                  <p className="text-xs font-medium text-foreground">{officerMap[ci.officer_id] || "Officer"}</p>
                  <p className="text-[10px] text-muted-foreground">{ci.site_id ? siteMap[ci.site_id] || "Site" : "—"}</p>
                </div>
                <span className="text-[10px] text-muted-foreground">{timeAgo(ci.checked_at)}</span>
              </div>
            ))}
            {rawCheckIns.length === 0 && <p className="text-xs text-muted-foreground">No check-ins</p>}
          </div>
        );
      case "incidents":
        const severityStyle: Record<string, string> = {
          low: "text-[hsl(var(--app-gold))]",
          medium: "text-orange-400",
          high: "text-[hsl(var(--app-red))]",
          critical: "text-red-300",
        };
        return (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {rawIncidents.slice(0, 20).map(inc => (
              <div key={inc.id} className="flex items-center justify-between py-1.5 border-b border-[hsl(var(--app-border-subtle))] last:border-0">
                <div className="min-w-0 flex-1 mr-3">
                  <p className="text-xs font-medium text-foreground truncate">{inc.title}</p>
                  <p className="text-[10px] text-muted-foreground">{inc.site_id ? siteMap[inc.site_id] || "Site" : "—"}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className={`text-[10px] font-semibold uppercase ${severityStyle[inc.severity] || "text-muted-foreground"}`}>{inc.severity}</span>
                  <p className="text-[9px] text-muted-foreground">{timeAgo(inc.created_at)}</p>
                </div>
              </div>
            ))}
            {rawIncidents.length === 0 && <p className="text-xs text-muted-foreground">No incidents</p>}
          </div>
        );
      case "tickets":
        const openTickets = tickets.filter(t => t.status === "open");
        return (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {openTickets.slice(0, 15).map(t => (
              <div key={t.id} className="flex items-center justify-between py-1.5 border-b border-[hsl(var(--app-border-subtle))] last:border-0">
                <div className="min-w-0 flex-1 mr-3">
                  <p className="text-xs font-medium text-foreground truncate">{t.subject}</p>
                  <p className="text-[10px] text-muted-foreground">{t.user_name || t.user_email || "User"} · {t.user_role?.replace("_", " ") || ""}</p>
                </div>
                <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(t.created_at)}</span>
              </div>
            ))}
            {openTickets.length === 0 && <p className="text-xs text-muted-foreground">No open tickets</p>}
          </div>
        );
      default:
        return null;
    }
  };

  const activityIcon = (type: string) => {
    switch (type) {
      case "check_in": return <CheckCircle2 className="w-4 h-4 text-[hsl(var(--app-green))]" />;
      case "incident": return <AlertTriangle className="w-4 h-4 text-[hsl(var(--app-red))]" />;
      case "face_verify": return <Eye className="w-4 h-4 text-[hsl(var(--app-blue))]" />;
      default: return <Activity className="w-4 h-4 text-[hsl(var(--app-gold))]" />;
    }
  };

  const roleBadge = (role: string) => {
    const colors: Record<string, string> = {
      business_owner: "bg-[hsl(var(--app-gold))]/20 text-[hsl(var(--app-gold))]",
      site_owner: "bg-[hsl(var(--app-blue))]/20 text-[hsl(var(--app-blue))]",
      officer: "bg-[hsl(var(--app-green))]/20 text-[hsl(var(--app-green))]",
      admin: "bg-[hsl(var(--app-red))]/20 text-[hsl(var(--app-red))]",
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${colors[role] || "bg-muted text-muted-foreground"}`}>
        {role.replace("_", " ")}
      </span>
    );
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      open: "bg-[hsl(var(--app-gold))]/20 text-[hsl(var(--app-gold))]",
      resolved: "bg-[hsl(var(--app-green))]/20 text-[hsl(var(--app-green))]",
      closed: "bg-muted text-muted-foreground",
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${colors[status] || "bg-muted text-muted-foreground"}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--app-bg))] flex items-center justify-center font-outfit">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-xl border-2 border-[hsl(var(--app-gold))] flex items-center justify-center animate-pulse"
            style={{ boxShadow: "0 0 20px hsla(var(--app-gold-glow), 0.2)" }}>
            <Shield className="w-6 h-6 text-[hsl(var(--app-gold))]" />
          </div>
          <p className="text-sm text-muted-foreground">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--app-bg))] text-foreground font-outfit">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[hsl(var(--app-surface-1))]/95 backdrop-blur border-b border-[hsl(var(--app-border-subtle))]">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={secureflexLogo} alt="SecureFlex" className="h-8" />
            <div className="h-5 w-px bg-[hsl(var(--app-border-subtle))]" />
            <span className="text-xs font-semibold text-[hsl(var(--app-gold))] uppercase tracking-widest">Admin Panel</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden sm:block">{profile?.full_name || user?.email}</span>
            <ThemeToggle />
            <button onClick={handleSignOut} className="p-2 rounded-lg hover:bg-[hsl(var(--app-surface-2))] text-muted-foreground hover:text-foreground transition">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Tab Nav */}
      <nav className="bg-[hsl(var(--app-surface-1))]/60 border-b border-[hsl(var(--app-border-subtle))]">
        <div className="max-w-7xl mx-auto px-4 flex gap-1 overflow-x-auto py-1">
          {adminTabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-medium transition whitespace-nowrap ${
                tab === t.id
                  ? "bg-[hsl(var(--app-gold))]/15 text-[hsl(var(--app-gold))] border border-[hsl(var(--app-gold))]/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--app-surface-2))]"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
              {t.id === "support" && stats.totalTicketsOpen > 0 && (
                <span className="ml-1 bg-[hsl(var(--app-red))] text-white text-[10px] px-1.5 py-0.5 rounded-full">{stats.totalTicketsOpen}</span>
              )}
            </button>
          ))}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {/* MAP TAB */}
        {tab === "map" && (
          <AdminMapView
            officers={rawOfficers}
            sites={rawSites}
            checkpoints={rawCheckpoints}
            onCheckpointMoved={async (id, lat, lng) => {
              const { error } = await supabase.from("checkpoints").update({ lat, lng }).eq("id", id);
              if (error) { toast.error("Failed to move checkpoint"); return; }
              toast.success("Checkpoint repositioned — site owner will see the update");
              setRawCheckpoints(prev => prev.map(cp => cp.id === id ? { ...cp, lat, lng } : cp));
            }}
          />
        )}

        {/* OVERVIEW TAB */}
        {tab === "overview" && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {statCards.map((s, i) => {
                const isExpanded = expandedTile === s.id;
                return (
                  <div key={i}
                    onClick={() => setExpandedTile(isExpanded ? null : s.id)}
                    className={`bg-[hsl(var(--app-surface-1))] border rounded-xl p-4 cursor-pointer transition-all hover:border-[hsl(var(--app-border-active))] ${
                      isExpanded ? "border-[hsl(var(--app-gold))]/50 col-span-2 sm:col-span-2" : "border-[hsl(var(--app-border-subtle))]"
                    }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <s.icon className="w-4 h-4" style={{ color: s.color }} />
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{s.label}</span>
                      </div>
                      <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </div>
                    <p className="text-2xl font-bold font-bebas tracking-wide" style={{ color: s.color }}>{s.value}</p>
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-[hsl(var(--app-border-subtle))]">
                        {renderTileDetail(s.id)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Check-in Trends */}
              <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl p-5">
                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[hsl(var(--app-green))]" /> Check-in Trends (14 Days)
                </h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={checkInTrend}>
                      <defs>
                        <linearGradient id="checkInGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(160, 75%, 39%)" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="hsl(160, 75%, 39%)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 40%, 14%)" />
                      <XAxis dataKey="date" tick={{ fill: "hsl(215, 15%, 55%)", fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: "hsl(215, 15%, 55%)", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "hsl(222, 50%, 7%)", border: "1px solid hsl(220, 40%, 14%)", borderRadius: 8, fontSize: 12 }}
                        labelStyle={{ color: "hsl(210, 20%, 90%)" }}
                        itemStyle={{ color: "hsl(160, 75%, 39%)" }}
                      />
                      <Area type="monotone" dataKey="checkIns" stroke="hsl(160, 75%, 39%)" strokeWidth={2} fill="url(#checkInGrad)" name="Check-ins" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Incidents by Severity */}
              <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl p-5">
                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-[hsl(var(--app-red))]" /> Incidents by Severity
                </h3>
                <div className="h-56 flex items-center justify-center">
                  {incidentsBySeverity.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={incidentsBySeverity}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={4}
                          dataKey="value"
                          nameKey="name"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {incidentsBySeverity.map((_, i) => (
                            <Cell key={i} fill={SEVERITY_COLORS[i % SEVERITY_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ backgroundColor: "hsl(222, 50%, 7%)", border: "1px solid hsl(220, 40%, 14%)", borderRadius: 8, fontSize: 12 }}
                        />
                        <Legend wrapperStyle={{ fontSize: 11, color: "hsl(215, 15%, 55%)" }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-xs text-muted-foreground">No incidents recorded</p>
                  )}
                </div>
              </div>
            </div>

            {/* Officer Activity Heatmap */}
            <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl p-5">
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Users className="w-4 h-4 text-[hsl(var(--app-gold))]" /> Top Officer Activity (Check-ins)
              </h3>
              <div className="h-56">
                {officerActivityHeatmap.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={officerActivityHeatmap} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 40%, 14%)" horizontal={false} />
                      <XAxis type="number" tick={{ fill: "hsl(215, 15%, 55%)", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" tick={{ fill: "hsl(215, 15%, 55%)", fontSize: 10 }} axisLine={false} tickLine={false} width={90} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "hsl(222, 50%, 7%)", border: "1px solid hsl(220, 40%, 14%)", borderRadius: 8, fontSize: 12 }}
                        itemStyle={{ color: "hsl(42, 87%, 55%)" }}
                      />
                      <Bar dataKey="checkIns" fill="hsl(42, 87%, 55%)" radius={[0, 4, 4, 0]} name="Check-ins" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-xs text-muted-foreground text-center py-8">No officer data available</p>
                )}
              </div>
            </div>

            {/* Country breakdown */}
            <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl p-5">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4 text-[hsl(var(--app-blue))]" /> Users by Country
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {Object.entries(
                  users.reduce((acc, u) => {
                    const c = u.country || "Unknown";
                    acc[c] = (acc[c] || 0) + 1;
                    return acc;
                  }, {} as Record<string, number>)
                ).sort((a, b) => b[1] - a[1]).map(([country, count]) => (
                  <div key={country} className="flex items-center justify-between bg-[hsl(var(--app-surface-2))] rounded-lg px-3 py-2">
                    <span className="text-xs text-foreground">{country}</span>
                    <span className="text-xs font-bold text-[hsl(var(--app-gold))]">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity Preview */}
            <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[hsl(var(--app-gold))]" /> Recent Activity
                </h3>
                <button onClick={() => setTab("activity")} className="text-[10px] text-[hsl(var(--app-gold))] hover:underline">View All →</button>
              </div>
              <div className="space-y-2">
                {activities.slice(0, 8).map(a => (
                  <div key={a.id} className="flex items-center gap-3 py-2 border-b border-[hsl(var(--app-border-subtle))] last:border-0">
                    {activityIcon(a.type)}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground truncate">{a.description}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {a.user_name && <span>{a.user_name}</span>}
                        {a.site_name && <span> · {a.site_name}</span>}
                      </p>
                    </div>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">{timeAgo(a.timestamp)}</span>
                  </div>
                ))}
                {activities.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No recent activity</p>}
              </div>
            </div>
          </>
        )}

        {/* USERS TAB */}
        {tab === "users" && (
          <>
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="flex gap-2 flex-wrap">
                {["all", "business_owner", "site_owner", "officer"].map(f => (
                  <button key={f} onClick={() => setUserFilter(f)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                      userFilter === f
                        ? "bg-[hsl(var(--app-gold))]/15 text-[hsl(var(--app-gold))] border border-[hsl(var(--app-gold))]/30"
                        : "bg-[hsl(var(--app-surface-2))] text-muted-foreground hover:text-foreground"
                    }`}>
                    {f === "all" ? "All" : f.replace("_", " ")}
                    <span className="ml-1.5 text-[10px] opacity-70">
                      ({f === "all" ? users.length : users.filter(u => u.role === f).length})
                    </span>
                  </button>
                ))}
              </div>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search name, email, country..."
                  className="w-full bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))] rounded-lg pl-9 pr-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[hsl(var(--app-gold))]/50"
                />
              </div>
            </div>

            <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))]/50">
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium">Name</th>
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium">Email</th>
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium">Role</th>
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium">Country</th>
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium">Last Login</th>
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u.id} className="border-b border-[hsl(var(--app-border-subtle))] hover:bg-[hsl(var(--app-surface-2))]/30 transition">
                        <td className="px-4 py-3 text-foreground font-medium">{u.full_name || "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground">{u.email || "—"}</td>
                        <td className="px-4 py-3">{roleBadge(u.role)}</td>
                        <td className="px-4 py-3 text-muted-foreground">{u.country || "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground">{u.last_login_at ? timeAgo(u.last_login_at) : "Never"}</td>
                        <td className="px-4 py-3 text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No users found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* SYSTEM ADMINS TAB */}
        {tab === "system-admins" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bebas text-foreground tracking-wide flex items-center gap-2">
                <Shield className="w-5 h-5 text-[hsl(var(--app-gold))]" /> System Administrators
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-4">
                <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))]/50">
                    <h4 className="font-semibold text-sm">Active Administrators ({users.filter(u => u.role === 'admin').length})</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-[hsl(var(--app-border-subtle))] text-left text-muted-foreground">
                          <th className="px-4 py-3 font-medium">Name</th>
                          <th className="px-4 py-3 font-medium">Email</th>
                          <th className="px-4 py-3 font-medium">Last Login</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.filter(u => u.role === 'admin').map(admin => (
                          <tr key={admin.id} className="border-b border-[hsl(var(--app-border-subtle))] hover:bg-[hsl(var(--app-surface-2))]/30 transition">
                            <td className="px-4 py-3 text-foreground font-medium">
                              {admin.full_name || "—"}
                              {(admin.email === "secureflex@outlook.com" || admin.email === "admin@lovable.dev") && (
                                <span className="ml-2 text-[9px] bg-[hsl(var(--app-gold))]/20 text-[hsl(var(--app-gold))] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">Primary</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">{admin.email}</td>
                            <td className="px-4 py-3 text-muted-foreground">{admin.last_login_at ? timeAgo(admin.last_login_at) : "Never"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl p-5 h-fit">
                <h4 className="font-semibold text-sm mb-4 text-foreground flex items-center gap-2">
                  <User className="w-4 h-4 text-[hsl(var(--app-gold))]" /> Add New Administrator
                </h4>
                <p className="text-[10px] text-muted-foreground mb-4">
                  Create a secondary admin account. They will be required to confirm their email address on their first login.
                </p>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.target as HTMLFormElement;
                  const data = new FormData(form);
                  const name = data.get("name") as string;
                  const email = data.get("email") as string;
                  const password = data.get("password") as string;
                  
                  if (!name || !email || !password) return toast.error("Please fill in all fields.");
                  if (password.length < 6) return toast.error("Password must be at least 6 characters.");
                  
                  const toastId = toast.loading("Creating system admin...");
                  try {
                    // Create an isolated client so the primary admin isn't actively logged out!
                    const { createClient } = await import('@supabase/supabase-js');
                    const tempClient = createClient(
                      import.meta.env.VITE_SUPABASE_URL,
                      import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY, // Note: Use publishable/anon key
                      { auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false } }
                    );

                    const { error } = await tempClient.auth.signUp({
                      email,
                      password,
                      options: {
                        data: {
                          full_name: name,
                          role: "admin",
                        }
                      }
                    });

                    if (error) throw error;
                    
                    toast.success("Admin account created successfully!", { id: toastId });
                    form.reset();
                    
                    // Add purely to the local state so it shows up instantly
                    setUsers(prev => [...prev, {
                      id: "temp-" + Date.now(),
                      full_name: name,
                      email: email,
                      country: "Unknown",
                      last_login_at: null,
                      created_at: new Date().toISOString(),
                      role: "admin"
                    }]);

                  } catch (err: any) {
                    toast.error(err.message || "Failed to create admin.", { id: toastId });
                  }
                }} className="space-y-4">
                  <div>
                    <input name="name" placeholder="Full Name" required className="w-full bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))] rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[hsl(var(--app-gold))]/50" />
                  </div>
                  <div>
                    <input name="email" type="email" placeholder="Email Address" required className="w-full bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))] rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[hsl(var(--app-gold))]/50" />
                  </div>
                  <div>
                    <input name="password" type="password" placeholder="Temporary Password" required className="w-full bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))] rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[hsl(var(--app-gold))]/50" />
                  </div>
                  <button type="submit" className="w-full bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-bold py-2 rounded-lg text-xs hover:brightness-110 transition gold-glow flex justify-center items-center gap-2">
                    <Shield className="w-3.5 h-3.5" /> Create Admin
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* ACTIVITY TAB */}
        {tab === "activity" && (
          <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Activity className="w-4 h-4 text-[hsl(var(--app-gold))]" /> Platform Activity Feed
            </h3>
            <div className="space-y-1">
              {activities.map(a => (
                <div key={a.id} className="flex items-start gap-3 py-3 border-b border-[hsl(var(--app-border-subtle))] last:border-0">
                  <div className="mt-0.5">{activityIcon(a.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground">{a.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {a.user_name && <span className="text-[10px] text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" />{a.user_name}</span>}
                      {a.site_name && <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Building2 className="w-3 h-3" />{a.site_name}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">{timeAgo(a.timestamp)}</span>
                    <p className="text-[9px] text-muted-foreground/50">{new Date(a.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
              {activities.length === 0 && <p className="text-xs text-muted-foreground text-center py-8">No activity recorded yet</p>}
            </div>
          </div>
        )}

        {/* PROMOS TAB */}
        {tab === "promos" && <AdminPromoManager />}

        {/* SUPPORT TAB */}
        {tab === "support" && (
          <>
            <div className="flex gap-2 flex-wrap">
              {["open", "resolved", "all"].map(f => (
                <button key={f} onClick={() => setTicketFilter(f)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                    ticketFilter === f
                      ? "bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))] border border-[hsl(var(--app-gold)/0.3)]"
                      : "bg-[hsl(var(--app-surface-2))] text-muted-foreground hover:text-foreground border border-transparent"
                  }`}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                  <span className="ml-1.5 text-[10px] opacity-70">
                    ({f === "all" ? tickets.length : tickets.filter(t => t.status === f).length})
                  </span>
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {filteredTickets.map(ticket => (
                <div key={ticket.id} className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">{ticket.subject}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-muted-foreground">{ticket.user_name || ticket.user_email || "User"}</span>
                        {ticket.user_role && roleBadge(ticket.user_role)}
                        <span className="text-[10px] text-muted-foreground">· {timeAgo(ticket.created_at)}</span>
                      </div>
                    </div>
                    {statusBadge(ticket.status)}
                  </div>
                  <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{ticket.message}</p>

                  {ticket.admin_response && (
                    <div className="bg-[hsl(var(--app-surface-2))] rounded-lg p-3 mb-3 border-l-2 border-[hsl(var(--app-gold))]">
                      <p className="text-[10px] text-[hsl(var(--app-gold))] font-semibold mb-1">Admin Response</p>
                      <p className="text-xs text-foreground">{ticket.admin_response}</p>
                      {ticket.responded_at && <p className="text-[9px] text-muted-foreground mt-1">{timeAgo(ticket.responded_at)}</p>}
                    </div>
                  )}

                  {ticket.status === "open" && (
                    <>
                      {replyingTo === ticket.id ? (
                        <div className="space-y-2">
                          <textarea
                            value={replyText}
                            onChange={e => setReplyText(e.target.value)}
                            placeholder="Type your response..."
                            className="w-full bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))] rounded-lg p-3 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[hsl(var(--app-gold))]/50 min-h-[80px] resize-none"
                          />
                          <div className="flex gap-2">
                            <button onClick={() => handleReply(ticket.id)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] rounded-lg text-xs font-semibold hover:opacity-90 transition">
                              <Send className="w-3 h-3" /> Send & Resolve
                            </button>
                            <button onClick={() => { setReplyingTo(null); setReplyText(""); }}
                              className="px-3 py-1.5 bg-[hsl(var(--app-surface-2))] text-muted-foreground rounded-lg text-xs hover:text-foreground transition">
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => setReplyingTo(ticket.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-gold))] rounded-lg text-xs font-medium hover:bg-[hsl(var(--app-gold))]/10 transition">
                          <MessageSquare className="w-3 h-3" /> Reply
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
              {filteredTickets.length === 0 && (
                <div className="bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-xl p-8 text-center">
                  <Inbox className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">No {ticketFilter} tickets</p>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
